import React, { useState } from "react";
import PropTypes from "prop-types";
import Toast from "bootstrap/js/dist/toast";
import { snakeCase } from "change-case";

import csrftoken from "~/shared/prefilter";
import { objectToString } from "~/shared/utils";
import DatePicker from "../components/DatePicker/DatePicker";

const RequiredIndicator = () => <span className = "text-danger ms-1">*</span>;

const DatasetRequestApp = (props) => {
    const [data, setData] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [success, setSuccess] = useState();
    const [error, setError] = useState();

    const handleSubmit = (event) => {
        event.preventDefault();

        const formData = new FormData();

        Object.entries(data).forEach(([key, value]) => {
            if (key !== 'logo') {
                if(key === 'tags' && Array.isArray(value))
                    value.forEach((tag) => { formData.append("tags", tag); });
                else if(key === 'contributors' && Array.isArray(value))
                    value.forEach((contributor) => { formData.append("contributors", contributor); });
                else
                    formData.append(snakeCase(key), value);
            }
        });

        if (data.logo)
            formData.append('logo', data.logo);

        setIsUploading(true);
        fetch(props.datasetRequestApiEndpoint, {
            method: 'POST',
            body: formData,
            headers: { 'X-CSRFToken': csrftoken },
            credentials: 'same-origin'
        })
        .then((response) => {
            if (!response.ok)
                return response.json().then(err => { throw err; });
            return response.json();
        })
        .then((response) => {
            setIsUploading(false);
            setSuccess(response.message);
            console.log('Success:', response.message);
        })
        .catch((error) => {
            setIsUploading(false);
            setError(objectToString(error).join('\n'));
            Toast.getOrCreateInstance($("#error-toast")[0]).show();
            console.error('Error:', error);
        });
    };

    const handleCreatedDateUpdate = (date) => setData((prev) => ({ ...prev, ...{ createdAt: new Date(date).toISOString() } }));
    const handleCreatedDateReset = () => setData(({ createdAt, ...rest }) => rest);  /* eslint-disable-line */

    const handleLastUpdatedDateUpdate = (date) => setData((prev) => ({ ...prev, ...{ lastUpdated: new Date(date).toISOString() } }));
    const handleLastUpdatedDateReset = () => setData(({ lastUpdated, ...rest }) => rest);  /* eslint-disable-line */

    const handleLogoUpdate = (event) => {
        if (event.target.files.length > 0)
            setData((prev) => ({ ...prev, logo: event.target.files[0] }));
        else
            setData(({ logo, ...rest }) => rest);  /* eslint-disable-line */
    };

    const handleContributorsAdd = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.target.value.trim()) {
                const newContributor = event.target.value.trim();
                if (!data.contributors || !data.contributors.includes(newContributor))
                    setData((prev) => ({ ...prev, ...{ contributors: [...(prev.contributors || []), newContributor] } }));
            }
            event.target.value = '';
        }
    };
    const handleTagsAdd = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.target.value.trim()) {
                const newTag = event.target.value.trim();
                if (!data.tags || !data.tags.includes(newTag))
                    setData((prev) => ({ ...prev, ...{ tags: [...(prev.tags || []), newTag] } }));
            }
            event.target.value = '';
        }
    };

    return !success ? <>
        <div id = "error-toast" className = "toast align-items-center text-bg-danger border-0" role = "alert" aria-live = "assertive" aria-atomic = "true">
            <div className = "d-flex">
                <div className = "toast-body">{ error }</div>
                <button type = "button" className = "btn-close me-2 m-auto" data-bs-dismiss = "toast" aria-label = "Close"></button>
            </div>
        </div>
        <p className = "text-center fst-italic mb-3">Please fill out the form below to add your dataset to the database.</p>
        <form onSubmit = { handleSubmit } className = "my-3">
            <div className = "border border-bottom-0 border-start-0 border-end-0 py-3 text-center">
                <h5>Who are you?</h5>
                <div className = "form-floating my-3 fs-6">
                    <input
                        type = "email"
                        name = "email"
                        className = "form-control"
                        placeholder = "Email address"
                        id = "email"
                        onChange = { (e) => setData({ ...data, ...{ email: e.target.value } }) }
                        required
                    />
                    <label htmlFor = "email">
                        Email address
                        <RequiredIndicator />
                    </label>
                </div>
            </div>
            <div className = "border border-bottom-0 border-start-0 border-end-0 py-3 text-center">
                <h5>General information</h5>
                <div className = "form-floating my-3 fs-6">
                    <input
                        type = "text"
                        name = "title"
                        className = "form-control"
                        placeholder = "Title"
                        id = "title"
                        onChange = { (e) => setData({ ...data, ...{ title: e.target.value } }) }
                        required
                    />
                    <label htmlFor = "title">
                        Title
                        <RequiredIndicator />
                    </label>
                </div>
                <div className = "row my-3 fs-6">
                    <div className = "col-12 col-md-4">
                        <div className = "form-floating">
                            <input
                                type = "text"
                                name = "contributors"
                                className = "form-control"
                                placeholder = "Contributors"
                                id = "contributors"
                                maxLength = { 64 }
                                onKeyDown = { handleContributorsAdd }
                            />
                            <label htmlFor = "contributors">Contributors</label>
                        </div>
                    </div>
                    <div className = "col-12 col-md-8 d-flex align-items-center overflow-auto">
                        {
                            data.contributors?.map((contributor, idx) => (
                                <span key = { idx } className = "badge bg-primary rounded-pill my-2 me-2">
                                    { contributor }
                                    <i className = "bi bi-x-lg btn btn-sm btn-outline-light border-0 bg-transparent p-0 ms-1" onClick = { () => { setData({ ...data, ...{ contributors: data.contributors.filter(c => c !== contributor) } }); } }></i>
                                </span>
                            ))
                        }
                    </div>
                </div>
                <div className = "d-flex align-items-center my-3">
                    <label htmlFor = "logo" className = "form-label text-nowrap me-2 my-0 fs-6">Logo</label>
                    <input
                        type = "file"
                        name = "logo"
                        id = "logo"
                        className = "form-control flex-grow-1"
                        accept = "image/*"
                        onChange = { handleLogoUpdate }
                    />
                </div>
                <div className = "form-floating my-3 fs-6">
                    <input
                        type = "text"
                        name = "url"
                        className = "form-control"
                        placeholder = "URL"
                        id = "url"
                        maxLength = { 255 }
                        onChange = { (e) => setData({ ...data, ...{ url: e.target.value } }) }
                        required
                    />
                    <label htmlFor = "url">
                        URL
                        <RequiredIndicator />
                    </label>
                </div>

                <div className = "form-floating my-3 fs-6">
                    <input
                        type = "number"
                        name = "size"
                        className = "form-control"
                        placeholder = "Size"
                        id = "size"
                        min = { 0 }
                        onChange = { (e) => setData({ ...data, ...{ size: e.target.value } }) }
                    />
                    <label htmlFor = "size">Size (MB)</label>
                </div>
                <div className = "row my-3 fs-6">
                    <div className = "col-12 col-md-4">
                        <div className = "form-floating">
                            <input
                                type = "text"
                                name = "tags"
                                className = "form-control"
                                placeholder = "Tags"
                                id = "tags"
                                maxLength = { 64 }
                                onKeyDown = { handleTagsAdd }
                            />
                            <label htmlFor = "tags">Tags</label>
                        </div>
                    </div>
                    <div className = "col-12 col-md-8 d-flex align-items-center overflow-auto">
                        {
                            data.tags?.map((tag, idx) => (
                                <span key = { idx } className = "badge bg-primary rounded-pill my-2 me-2">
                                    { tag }
                                    <i className = "bi bi-x-lg btn btn-sm btn-outline-light border-0 bg-transparent p-0 ms-1" onClick = { () => { setData({ ...data, ...{ tags: data.tags.filter(t => t !== tag) } }); } }></i>
                                </span>
                            ))
                        }
                    </div>
                </div>
            </div>
            <div className = "border border-start-0 border-end-0 py-3 text-center">
                <h5 className = "text-center">Dataset description</h5>
                <div className = "form-floating my-3 fs-6">
                    <input
                        type = "text"
                        name = "headline"
                        className = "form-control"
                        placeholder = "Headline"
                        id = "headline"
                        onChange = { (e) => setData({ ...data, ...{ headline: e.target.value } }) }
                        required
                    />
                    <label htmlFor = "headline">
                        Headline
                        <RequiredIndicator />
                    </label>
                </div>
                <div className = "form-floating my-3 fs-6">
                    <textarea
                        name = "description"
                        className = "form-control"
                        placeholder = "Description"
                        id = "description"
                        onChange = { (e) => setData({ ...data, ...{ description: e.target.value } }) }
                    />
                    <label htmlFor = "description">Description</label>
                </div>
                <div className = "d-flex justify-content-evenly align-items-center flex-wrap fs-6">
                    <div>
                        <label className = "form-label me-2 d-inline-block" htmlFor = "created">Created:</label>
                        <DatePicker name = "created" updateDate = { handleCreatedDateUpdate } resetDate = { handleCreatedDateReset } />
                    </div>
                    <div>
                        <label className = "form-label me-2 d-inline-block" htmlFor = "lastUpdated">Last updated:</label>
                        <DatePicker name = "lastUpdated" updateDate = { handleLastUpdatedDateUpdate } resetDate = { handleLastUpdatedDateReset } />
                    </div>
                </div>
            </div>
            
            <div className = "d-flex justify-content-center align-items-center my-3">
                {
                    isUploading ?
                    <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div> :
                    <button type = "submit" className = "btn btn-lg btn-primary text-light">Submit</button>
                }
            </div>
        </form>
    </> : <p className = "text-center text-success">{ success }</p>;
};

DatasetRequestApp.propTypes = {
    datasetRequestApiEndpoint: PropTypes.string,
};

export default DatasetRequestApp;